import * as Yup from 'yup';

import { AdvanceAccess } from 'config';
import { PayoutType, ExpensesType } from 'types';

import { FormValues, GetInitialValues, ExpenseTypeCashStatus } from './types';

export const cashLocationIncompatibleTypes = [ExpensesType.Reimbursement];

const defaultExpenseTypeValues = Object.values(ExpensesType).filter(
  (value) => value !== ExpensesType.Misc
);

export const hideReceiptFieldTypes: ExpensesType[] = [
  ExpensesType.Misc,
  ExpensesType.Bonus,
  ExpensesType.Advance,
];

export const getAvailableExpenseTypeValues = ({
  customTypeValues,
  isOnlyCashLocation,
}: {
  isOnlyCashLocation?: boolean;
  customTypeValues?: ExpensesType[];
}) => {
  const values = customTypeValues || defaultExpenseTypeValues;

  if (!isOnlyCashLocation) {
    return values;
  }

  return values.filter(
    (value) => !cashLocationIncompatibleTypes.includes(value)
  );
};

export const shouldShowCashFromEvent = ({
  type,
  hideCashFromEvent,
}: {
  type?: ExpensesType;
  hideCashFromEvent?: boolean;
}) => !hideCashFromEvent && !cashLocationIncompatibleTypes.includes(type!);

// Owner 26 Oct 2026: a Manager / Admin Rep records only cash handed out at
// the show, from its register.
export const CASH_ONLY_ADVANCE_LABEL =
  "Cash handed out from the show's register";

/** Pay screens' "Add row": only the owner and the office add a pay advance. */
export const typeValuesForAdvanceAccess = <T extends string>(
  values: T[],
  access: AdvanceAccess
): T[] =>
  access === AdvanceAccess.Full
    ? values
    : values.filter((value) => value !== ExpensesType.Advance);

/** A cash advance above the show's register cash (compared in cents). */
export const isOverRegisterCash = ({
  amount,
  balance,
  isAdvance,
  isCashFromEvent,
}: {
  balance?: number;
  isAdvance?: boolean;
  amount?: string | number;
  isCashFromEvent?: boolean;
}) =>
  !!isAdvance &&
  !!isCashFromEvent &&
  typeof balance === 'number' &&
  Math.round(Number(amount || 0) * 100) > Math.round(balance * 100);

export const sanitizePayoutFormValues = (values: FormValues): FormValues => {
  if (!cashLocationIncompatibleTypes.includes(values.type!)) {
    return values;
  }

  return {
    ...values,
    isCashFromEvent: false,
  };
};

export const getInitialValues = ({
  eventId,
  employeeId,
  initialValues,
  isOnlyCashLocation,
}: GetInitialValues): FormValues => ({
  eventId,
  date: '',
  notes: '',
  amount: '',
  categoryId: '',
  receipt: undefined,
  employeeId: employeeId || '',
  isCashFromEvent: isOnlyCashLocation || false,
  type: isOnlyCashLocation ? ExpensesType.Bonus : undefined,
  ...(initialValues?.notes !== undefined ? { notes: initialValues.notes } : {}),
  ...(initialValues?.amount !== undefined
    ? { amount: initialValues.amount }
    : {}),
  ...(initialValues?.date
    ? { date: initialValues.date as unknown as string }
    : {}),
});

const getRequiredValidationByType = ({
  message,
  withReceipt,
}: {
  message?: string;
  withReceipt?: boolean;
}) => ({
  then: (schema: Yup.MixedSchema | Yup.NumberSchema) =>
    schema.required(message),
  otherwise: (schema: Yup.MixedSchema | Yup.NumberSchema) =>
    schema.notRequired(),
  is: (type?: ExpensesType) =>
    type &&
    withReceipt &&
    !hideReceiptFieldTypes.includes(type as ExpensesType),
});

export const getValidationSchema = ({
  withReceipt,
  cashFlowBalance,
  showSelectExpensesType,
}: {
  withReceipt?: boolean;
  cashFlowBalance?: number;
  showSelectExpensesType?: boolean;
}) => {
  const typeValidation = showSelectExpensesType
    ? Yup.string().required()
    : Yup.string();

  const baseAmountValidation = Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === '-' || originalValue === '') {
        return undefined;
      }
      return value;
    })
    .required('Amount is required')
    .test(
      'is-valid-number',
      'Amount must be a valid number',
      (value) => value !== undefined && !isNaN(value)
    );

  const amountValidation = baseAmountValidation.when('type', {
    is: (type: ExpensesType) => type === ExpensesType.Misc,
    then: (schema) =>
      schema.test(
        'misc-amount',
        'Amount cannot be zero',
        (value) => value !== 0
      ),
    otherwise: (schema) =>
      schema.positive('Amount must be positive').when('isCashFromEvent', {
        is: true,
        otherwise: (schema) => schema,
        then: (schema) =>
          typeof cashFlowBalance === 'number'
            ? schema.max(
                cashFlowBalance,
                `Amount cannot exceed cash balance (${cashFlowBalance})`
              )
            : schema,
      }),
  });

  return Yup.object().shape({
    type: typeValidation,
    amount: amountValidation,
    date: Yup.date().required(),
    isCashFromEvent: Yup.boolean(),
    eventId: Yup.number().required(),
    employeeId: Yup.number().required(),
    notes: Yup.string().min(5).max(200).required(),
    categoryId: Yup.number().when(
      'type',
      getRequiredValidationByType({ withReceipt })
    ),
    receipt: Yup.mixed().when(
      'type',
      getRequiredValidationByType({
        withReceipt,
        message: 'File is a required field',
      })
    ),
  });
};

export const expenseTypeToPayoutMapping: Record<
  ExpensesType,
  Record<ExpenseTypeCashStatus, PayoutType>
> = {
  [ExpensesType.Misc]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Misc,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.Misc,
  },
  [ExpensesType.Bonus]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Bonus,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidBonus,
  },
  [ExpensesType.Advance]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Advance,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidAdvance,
  },
  [ExpensesType.Expense]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Expense,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidExpense,
  },
  [ExpensesType.Reimbursement]: {
    [ExpenseTypeCashStatus.Regular]: PayoutType.Reimbursements,
    [ExpenseTypeCashStatus.Prepaid]: PayoutType.PrepaidReimbursements,
  },
};
